import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useFeatures } from "@/lib/features";
import {
  Users, Network, CalendarCheck, CalendarRange, CheckSquare, ListTodo,
  FolderKanban, Inbox, UserPlus, LogOut, Laptop, FileText, Megaphone,
  MessageSquare, Sparkles, Building2, CalendarDays, Settings2, Wallet,
  Video, Layers, ChevronDown, ChevronRight, AlertTriangle, RotateCcw,
  LayoutDashboard, BookOpen, Briefcase, TrendingUp, ListChecks,
} from "lucide-react";

const MODULES = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, group: "Core" },
  { key: "inbox", label: "Inbox", icon: Inbox, group: "Core" },
  { key: "ai", label: "AI Assistant", icon: Sparkles, group: "Core" },

  { key: "people", label: "People Directory", icon: Users, group: "HR", desc: "Employee records & profiles" },
  { key: "org_chart", label: "Org Chart", icon: Network, group: "HR" },
  { key: "attendance", label: "Attendance", icon: CalendarCheck, group: "HR" },
  { key: "leaves", label: "Leaves", icon: CalendarRange, group: "HR" },
  { key: "approvals", label: "Approvals", icon: CheckSquare, group: "HR" },
  { key: "payroll", label: "Payroll", icon: Wallet, group: "HR" },
  { key: "pdf_payslip", label: "PDF Payslip", icon: FileText, group: "HR", desc: "Branded PDF generation" },

  { key: "tasks", label: "My Tasks", icon: ListTodo, group: "Work" },
  { key: "projects", label: "Projects", icon: FolderKanban, group: "Work" },
  { key: "meetings", label: "Meetings", icon: Video, group: "Work" },
  { key: "calendar", label: "Calendar", icon: CalendarDays, group: "Work" },

  { key: "onboarding", label: "Onboarding", icon: UserPlus, group: "Lifecycle" },
  { key: "offboarding", label: "Offboarding", icon: LogOut, group: "Lifecycle" },
  { key: "assets", label: "Assets", icon: Laptop, group: "Lifecycle" },
  { key: "recruitment", label: "Recruitment (ATS)", icon: Briefcase, group: "Lifecycle" },

  { key: "helpdesk", label: "Helpdesk", icon: ListChecks, group: "Services" },
  { key: "claims", label: "Claims & Reimbursements", icon: Wallet, group: "Services" },
  { key: "performance", label: "Performance & KPIs", icon: TrendingUp, group: "Services" },
  { key: "multi_level_approval", label: "Multi-level Approval", icon: CheckSquare, group: "Services", desc: "Chain approvers" },

  { key: "documents", label: "Documents", icon: FileText, group: "Knowledge" },
  { key: "announcements", label: "Announcements", icon: Megaphone, group: "Knowledge" },
  { key: "messaging", label: "Messaging", icon: MessageSquare, group: "Knowledge" },
  { key: "file_uploads", label: "File Uploads", icon: FileText, group: "Knowledge", desc: "Documents & receipts attachments" },

  { key: "holidays", label: "Holidays", icon: CalendarDays, group: "Admin" },
  { key: "organization", label: "Organization Setup", icon: Building2, group: "Admin" },
  { key: "audit_log", label: "Audit Log", icon: Settings2, group: "Admin" },
  { key: "feature_controls", label: "Feature Controls", icon: Settings2, group: "Admin" },

  { key: "forms", label: "Custom Forms", icon: FileText, group: "Future", comingSoon: true },
  { key: "reports", label: "Reports", icon: BookOpen, group: "Future", comingSoon: true },

  // Sprint 4 — modules that are visually present but not production-ready.
  // Toggle these OFF to hide them from tenant sidebars + dashboards entirely.
  { key: "lms",                label: "Learning (LMS)",          icon: BookOpen,    group: "Pending Backend", desc: "Course player not built", comingSoon: true },
  { key: "performance_pro",    label: "Performance Pro",          icon: TrendingUp,  group: "Pending Backend", desc: "Reviews / 360 / OKRs",     comingSoon: true },
  { key: "workflow_studio",    label: "Workflow Studio",          icon: Settings2,   group: "Pending Backend", desc: "Runtime engine pending",   comingSoon: true },
  { key: "ai_copilots",        label: "AI Copilots",              icon: Sparkles,    group: "Pending Backend", desc: "Recruiter / Wellbeing",    comingSoon: true },
  { key: "video_calls",        label: "Video Calls",              icon: Video,       group: "Pending Backend", desc: "WebRTC not integrated",    comingSoon: true },
  { key: "biometric_attendance", label: "Biometric / Facial / RFID", icon: Sparkles, group: "Pending Backend", desc: "Provider SDK pending",     comingSoon: true },
  { key: "google_sso",         label: "Google SSO",               icon: Users,       group: "Auth Pending",    desc: "OAuth keys not configured",comingSoon: true },
  { key: "microsoft_sso",      label: "Microsoft SSO",            icon: Users,       group: "Auth Pending",    desc: "Tenant ID not configured", comingSoon: true },
  { key: "find_workspace",     label: "Find Workspace",           icon: Building2,   group: "Auth Pending",    desc: "Multi-workspace finder",   comingSoon: true },
  { key: "form_16_pdf",        label: "Form 16 / PDF Generation", icon: FileText,    group: "Pending Backend", desc: "PDF rendering pending",    comingSoon: true },
];

const TAB_DEFS = {
  employee_profile: { label: "Employee Profile Tabs", items: [
    { k: "overview", l: "Overview" }, { k: "personal", l: "Personal" }, { k: "job", l: "Job & Joining" },
    { k: "reporting", l: "Reporting" }, { k: "responsibilities", l: "Responsibilities" }, { k: "skills", l: "Skills" },
    { k: "assets", l: "Assets" }, { k: "team", l: "Team" }, { k: "notes", l: "HR Notes" },
  ]},
  approvals: { label: "Approvals Tabs", items: [
    { k: "review", l: "Review" }, { k: "monitor", l: "Monitor" }, { k: "track", l: "Track" },
    { k: "escalated", l: "Escalated" }, { k: "sla", l: "SLA" },
  ]},
  documents: { label: "Documents Tabs", items: [
    { k: "policies", l: "Policies" }, { k: "forms", l: "Forms" },
    { k: "templates", l: "Letter Templates" }, { k: "employee_docs", l: "Employee Docs" },
  ]},
  tasks: { label: "Tasks Views", items: [
    { k: "list", l: "List" }, { k: "board", l: "Board" }, { k: "calendar", l: "Calendar" },
    { k: "timeline", l: "Timeline" }, { k: "activity", l: "Activity" }, { k: "files", l: "Files" }, { k: "chat", l: "Chat" },
  ]},
};

const SUB_DEFS = {
  attendance: { items: [
    { k: "checkin_checkout", l: "Check-In / Check-Out" }, { k: "live_hours", l: "Live hours counter" },
    { k: "monthly_calendar", l: "Monthly calendar" }, { k: "biometric", l: "Biometric source" },
    { k: "mobile", l: "Mobile attendance" }, { k: "web", l: "Web attendance" }, { k: "correction", l: "Attendance correction" },
  ]},
  leaves: { items: [
    { k: "apply", l: "Apply leave" }, { k: "balance", l: "Leave balance" }, { k: "team_calendar", l: "Team leave calendar" },
    { k: "attachments", l: "Attachments" }, { k: "multi_level_approval", l: "Multi-level approval" },
  ]},
  tasks: { items: [
    { k: "multi_assignee", l: "Multiple assignees" }, { k: "watchers", l: "Followers / watchers" },
    { k: "comments", l: "Comments" }, { k: "attachments", l: "Attachments" },
    { k: "subtasks", l: "Subtasks" }, { k: "notifications", l: "Notifications" }, { k: "board_view", l: "Board view" },
  ]},
  assets: { items: [
    { k: "issue", l: "Asset issue" }, { k: "return", l: "Asset return" },
    { k: "repairs", l: "Repairs" }, { k: "warranty", l: "Warranty tracking" },
    { k: "qr_code", l: "QR / barcode" }, { k: "history", l: "Asset history" },
  ]},
  messaging: { items: [
    { k: "dm", l: "Direct messages" }, { k: "channels", l: "Channels" },
    { k: "project_chat", l: "Project chat" }, { k: "floating_widget", l: "Floating chat widget" },
  ]},
  payroll: { items: [
    { k: "payslips", l: "Payslips" }, { k: "revisions", l: "Salary revisions" },
    { k: "bonuses", l: "Bonuses" }, { k: "deductions", l: "Deductions" }, { k: "employee_view", l: "Employee salary view" },
  ]},
};

const PRESETS = {
  basic:  { name: "Basic HR", desc: "People + Attendance + Leave + Approvals + Holidays",
    on: ["dashboard", "inbox", "people", "attendance", "leaves", "approvals", "holidays", "organization", "feature_controls"] },
  ops:    { name: "HR + Operations", desc: "Basic HR + Tasks + Projects + Assets + Docs + Helpdesk + Claims",
    on: ["dashboard", "inbox", "people", "org_chart", "attendance", "leaves", "approvals", "holidays", "tasks", "projects", "assets", "documents", "announcements", "messaging", "organization", "feature_controls", "helpdesk", "claims", "file_uploads"] },
  full:   { name: "Full Enterprise", desc: "Every module enabled incl. Payroll, Recruitment, Performance, Audit",
    on: MODULES.map(m => m.key).filter(k => !["forms", "reports"].includes(k)) },
  custom: { name: "Custom", desc: "Handpick exactly what your org needs", on: null },
};

export default function FeatureControls() {
  const { user } = useAuth();
  const { reload: reloadFeatures } = useFeatures();
  const [doc, setDoc] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/feature-flags").then((r) => {
      setDoc(r.data);
    });
  }, []);

  if (user?.role !== "super_admin") {
    return (
      <div className="p-8">
        <Card className="p-10 text-center border-amber-200 bg-amber-50">
          <Settings2 className="w-8 h-8 mx-auto text-amber-600 mb-3"/>
          <h2 className="font-[Outfit] font-semibold text-slate-900 text-lg">Super Admin only</h2>
          <p className="text-sm text-slate-600 mt-1">Feature Controls are reserved for Super Admin accounts.</p>
        </Card>
      </div>
    );
  }

  if (!doc) return <div className="p-8 text-slate-500">Loading…</div>;

  const update = (patch) => { setDoc({ ...doc, ...patch }); setDirty(true); };
  const toggleModule = (k) => update({ modules: { ...doc.modules, [k]: !doc.modules[k] }, preset: "custom" });
  const toggleTab = (mod, t) => update({
    tabs: { ...doc.tabs, [mod]: { ...(doc.tabs[mod]||{}), [t]: !(doc.tabs[mod]?.[t] !== false) } },
    preset: "custom",
  });
  const toggleSub = (mod, s) => update({
    sub_features: { ...doc.sub_features, [mod]: { ...(doc.sub_features[mod]||{}), [s]: !(doc.sub_features[mod]?.[s] !== false) } },
    preset: "custom",
  });

  const applyPreset = (key) => {
    const p = PRESETS[key];
    if (!p.on) { update({ preset: key }); return; }
    const modules = {};
    MODULES.forEach((m) => { modules[m.key] = p.on.includes(m.key); });
    update({ preset: key, modules });
    toast.success(`Applied preset: ${p.name}`);
  };

  const save = async () => {
    try {
      await api.patch("/feature-flags", { flags: {
        modules: doc.modules, tabs: doc.tabs, sub_features: doc.sub_features,
        preset: doc.preset, org_chart_template: doc.org_chart_template,
      }});
      toast.success("Feature flags saved and published");
      setDirty(false);
      reloadFeatures();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const groups = {};
  MODULES.forEach((m) => { (groups[m.group] ||= []).push(m); });
  const enabled = MODULES.filter(m => doc.modules[m.key]).length;
  const total = MODULES.length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="feature-controls-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Organization Configuration</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Feature Controls</h1>
          <p className="text-sm text-slate-600 mt-1">
            Shape QrewHR for your company · <span className="font-medium text-slate-800">{enabled}/{total}</span> modules active · preset: <span className="capitalize ml-1 inline-block px-2 py-0.5 rounded border border-slate-300 text-xs">{doc.preset}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <Button variant="outline" onClick={() => { api.get("/feature-flags").then(r => { setDoc(r.data); setDirty(false); }); }} data-testid="flags-reset"><RotateCcw className="w-4 h-4 mr-1"/>Reset</Button>}
          <Button onClick={save} disabled={!dirty} className="bg-slate-900 hover:bg-slate-800" data-testid="flags-save">
            {dirty ? "Save & Publish" : "Saved"}
          </Button>
        </div>
      </div>

      {/* Presets */}
      <Card className="p-4 border-cyan-500/20 bg-gradient-to-br from-cyan-50 to-white shadow-none">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-cyan-600"/>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">Quick presets</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(PRESETS).map(([k, p]) => (
            <button
              key={k}
              onClick={() => applyPreset(k)}
              data-testid={`preset-${k}`}
              className={`p-3 rounded-md border text-left transition ${doc.preset === k ? "border-cyan-500 bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:border-cyan-400"}`}
            >
              <div className="text-sm font-medium text-slate-900">{p.name}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{p.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      <Tabs defaultValue="modules">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="modules" data-testid="tab-modules">Modules</TabsTrigger>
          <TabsTrigger value="tabs" data-testid="tab-tabs">Tabs</TabsTrigger>
          <TabsTrigger value="sub" data-testid="tab-sub">Sub-features</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Role Visibility</TabsTrigger>
          <TabsTrigger value="chart" data-testid="tab-chart">Org Chart Template</TabsTrigger>
        </TabsList>

        {/* MODULES */}
        <TabsContent value="modules" className="mt-4 space-y-4">
          {Object.entries(groups).map(([g, items]) => (
            <Card key={g} className="p-5 border-slate-200 shadow-none">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{g}</div>
                <div className="text-[11px] text-slate-400">{items.filter(i => doc.modules[i.key]).length}/{items.length} active</div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((m) => {
                  const on = !!doc.modules[m.key];
                  const hasSub = SUB_DEFS[m.key] || TAB_DEFS[m.key];
                  const open = !!expanded[m.key];
                  return (
                    <div key={m.key}
                      className={`rounded-md border transition ${on ? "border-cyan-500/30 bg-cyan-500/5" : "border-slate-200 bg-slate-50"}`}
                      data-testid={`flag-${m.key}`}>
                      <div className="flex items-center gap-3 p-3">
                        <div className={`p-2 rounded-md ${on ? "bg-white" : "bg-slate-100"}`}>
                          <m.icon className={`w-4 h-4 ${on ? "text-cyan-600" : "text-slate-400"}`}/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${on ? "text-slate-900" : "text-slate-500"}`}>
                            {m.label}
                            {m.comingSoon && <Badge variant="outline" className="ml-2 text-[9px]">soon</Badge>}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{on ? "Enabled" : "Disabled"}</div>
                        </div>
                        <Switch checked={on} onCheckedChange={() => toggleModule(m.key)} />
                      </div>
                      {on && hasSub && (
                        <button
                          onClick={() => setExpanded({ ...expanded, [m.key]: !open })}
                          className="w-full px-3 py-1.5 text-[11px] text-slate-500 hover:text-slate-700 border-t border-slate-200 flex items-center justify-between bg-white/60"
                        >
                          <span>Fine-tune this module</span>
                          {open ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                        </button>
                      )}
                      {on && open && SUB_DEFS[m.key] && (
                        <div className="p-3 border-t border-slate-200 bg-white">
                          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">Sub-features</div>
                          <div className="grid grid-cols-2 gap-1.5">
                            {SUB_DEFS[m.key].items.map((s) => {
                              const sOn = (doc.sub_features[m.key]?.[s.k]) !== false;
                              return (
                                <label key={s.k} className="flex items-center gap-2 text-[12px] cursor-pointer">
                                  <Switch checked={sOn} onCheckedChange={() => toggleSub(m.key, s.k)} className="scale-75 origin-left"/>
                                  <span className={sOn ? "text-slate-800" : "text-slate-400"}>{s.l}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* TABS */}
        <TabsContent value="tabs" className="mt-4 space-y-4">
          {Object.entries(TAB_DEFS).map(([mod, def]) => (
            <Card key={mod} className="p-5 border-slate-200 shadow-none">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">{def.label}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {def.items.map((t) => {
                  const on = (doc.tabs[mod]?.[t.k]) !== false;
                  return (
                    <label key={t.k}
                      className={`flex items-center justify-between gap-2 p-2.5 rounded-md border cursor-pointer ${on ? "border-cyan-500/30 bg-cyan-500/5" : "border-slate-200 bg-slate-50"}`}
                      data-testid={`tab-${mod}-${t.k}`}>
                      <span className={`text-sm ${on ? "text-slate-900" : "text-slate-500"}`}>{t.l}</span>
                      <Switch checked={on} onCheckedChange={() => toggleTab(mod, t.k)} className="scale-75"/>
                    </label>
                  );
                })}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* SUB-FEATURES */}
        <TabsContent value="sub" className="mt-4 space-y-4">
          {Object.entries(SUB_DEFS).map(([mod, def]) => (
            <Card key={mod} className="p-5 border-slate-200 shadow-none">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{mod.replace("_", " ")}</div>
                {!doc.modules[mod] && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">Module off</Badge>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {def.items.map((s) => {
                  const on = (doc.sub_features[mod]?.[s.k]) !== false;
                  return (
                    <label key={s.k}
                      className={`flex items-center justify-between gap-2 p-2.5 rounded-md border cursor-pointer ${on ? "border-cyan-500/30 bg-cyan-500/5" : "border-slate-200 bg-slate-50"}`}>
                      <span className={`text-sm ${on ? "text-slate-900" : "text-slate-500"}`}>{s.l}</span>
                      <Switch checked={on} onCheckedChange={() => toggleSub(mod, s.k)} className="scale-75"/>
                    </label>
                  );
                })}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* ROLE VISIBILITY */}
        <TabsContent value="roles" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Role Visibility</div>
                <p className="text-xs text-slate-500 mt-1">Control which roles see each module in the sidebar. Leave untouched to use defaults. Uncheck all to hide.</p>
              </div>
              <Button
                size="sm" className="bg-slate-900 hover:bg-slate-800"
                onClick={async () => {
                  try {
                    await api.patch("/feature-flags/role-visibility", { role_visibility: doc.role_visibility || {} });
                    toast.success("Role visibility saved"); reload();
                  } catch { toast.error("Failed"); }
                }}
                data-testid="save-role-visibility"
              >Save</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-slate-500 py-2 pr-4 sticky left-0 bg-white">Module</th>
                    {["super_admin","hr_admin","manager","department_head","team_lead","finance","it_support","employee"].map(r => (
                      <th key={r} className="text-center text-[10px] uppercase tracking-wider font-semibold text-slate-500 py-2 px-2 whitespace-nowrap">{r.replace("_"," ")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(m => {
                    const rv = (doc.role_visibility || {})[m.key];
                    const allRoles = ["super_admin","hr_admin","manager","department_head","team_lead","finance","it_support","employee"];
                    const allowed = Array.isArray(rv) ? rv : allRoles;
                    return (
                      <tr key={m.key} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 pr-4 text-slate-800 font-medium sticky left-0 bg-white">{m.label}</td>
                        {allRoles.map(r => (
                          <td key={r} className="py-2 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={allowed.includes(r)}
                              onChange={(e) => {
                                const cur = Array.isArray(rv) ? [...rv] : [...allowed];
                                const next = e.target.checked ? [...new Set([...cur, r])] : cur.filter(x => x !== r);
                                update({ role_visibility: { ...(doc.role_visibility || {}), [m.key]: next } });
                              }}
                              data-testid={`rv-${m.key}-${r}`}
                              className="w-4 h-4 accent-cyan-600"
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ORG CHART TEMPLATE original */}
        <TabsContent value="chart" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Org Chart Visual Template</div>
            <p className="text-sm text-slate-600 mb-4">Choose how the org chart is displayed across the platform. Your employees will see this style.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { k: "tree", n: "Tree Flowchart", d: "Hierarchical flowchart with actual connecting lines. CEO → HR → Managers → Teams. Best for visualising reporting structure." },
                { k: "modern", n: "Modern", d: "Rounded cards with avatars and department color strip. Collapsible nodes." },
                { k: "classic", n: "Classic Tree", d: "Clean indented hierarchy with connectors. Best for deep orgs." },
                { k: "cards", n: "Photo Cards", d: "Large photo tiles grouped by department. Best for small teams." },
                { k: "compact", n: "Compact List", d: "Dense searchable list with title + manager. Scales for 1000+." },
              ].map((t) => (
                <button key={t.k} onClick={() => update({ org_chart_template: t.k })}
                  data-testid={`template-${t.k}`}
                  className={`p-4 rounded-md border text-left transition ${doc.org_chart_template === t.k ? "border-cyan-500 bg-cyan-500/5 shadow-sm" : "border-slate-200 hover:border-cyan-400 bg-white"}`}>
                  <div className="text-sm font-medium text-slate-900">{t.n}</div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{t.d}</div>
                  {doc.org_chart_template === t.k && <Badge className="mt-2 bg-cyan-600 text-white border-0">Selected</Badge>}
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="p-4 border-amber-300 bg-amber-50 shadow-none">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0"/>
          <div className="text-xs text-slate-700">
            <strong>Disabling a module hides it everywhere</strong> — sidebar, Create menu, quick links, dashboard widgets. Existing data is never deleted, only hidden. You can re-enable any module at any time.
          </div>
        </div>
      </Card>
    </div>
  );
}
